import { Badge } from "@/components/ui/badge"
import type { LiveBadgeKind } from "@/config/navigation"
import {
	useModerationCounts,
	usePendingCouponCampaignCount,
} from "@/queries/marketplace"
import { cn } from "@/utils/styles.helpers"

type LiveBadgeVariant = "badge" | "dot"

function ModerationOpenBadge({ variant }: { variant: LiveBadgeVariant }) {
	const { data } = useModerationCounts()
	if (!data || data.open === 0) return null

	const title = `${data.open} open · ${data.pendingConfirmation} need confirmation`
	const urgent = data.pendingConfirmation > 0

	if (variant === "dot") {
		return (
			<span
				className={cn(
					"absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none tabular-nums",
					urgent
						? "bg-destructive text-white"
						: "bg-sidebar-primary text-sidebar-primary-foreground",
				)}
				title={title}
			>
				{data.open}
			</span>
		)
	}

	return (
		<Badge
			variant={urgent ? "destructive" : "outline"}
			className="ml-auto h-5 px-1.5 text-xs tabular-nums"
			title={title}
		>
			{data.open}
		</Badge>
	)
}

function CouponRequestsPendingBadge({
	variant,
}: {
	variant: LiveBadgeVariant
}) {
	const { data } = usePendingCouponCampaignCount()
	if (!data) return null

	const title = `${data} coupon ${data === 1 ? "request" : "requests"} waiting for review`

	if (variant === "dot") {
		return (
			<span
				className="absolute -top-1.5 -right-2.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sidebar-primary px-1 text-[10px] font-semibold leading-none tabular-nums text-sidebar-primary-foreground"
				title={title}
			>
				{data}
			</span>
		)
	}

	return (
		<span
			className="ml-auto font-mono text-[11px] text-warn tabular-nums"
			title={title}
		>
			{data}
		</span>
	)
}

/**
 * Navigation badge fed by a polled count rather than a static label. The
 * `dot` variant is a small count pinned to the corner of a rail icon; its
 * parent must be positioned.
 */
export function LiveCountBadge({
	kind,
	variant = "badge",
}: {
	kind: LiveBadgeKind
	variant?: LiveBadgeVariant
}) {
	switch (kind) {
		case "moderationOpen":
			return <ModerationOpenBadge variant={variant} />
		case "couponRequestsPending":
			return <CouponRequestsPendingBadge variant={variant} />
	}
}
